/**
 * scheduleFteConfig.ts
 * GET /api/schedule/fte-config — the knobs behind the Phase-3 rough-FTE demand
 * strip: the admin-editable blended junior rate (AppSetting
 * `schedule.fte.hourlyRate`, default $200/h CAD) and the standard 40 h week.
 */

import apiClient from './apiClient';

export interface FteConfig {
  hourlyRate: number;
  hoursPerWeek: number;
}

export const fetchFteConfig = async (): Promise<FteConfig> => {
  const res = await apiClient.get('/schedule/fte-config');
  return res.data as FteConfig;
};

// ── P2 hybrid demand (Wave B, 2026-07-29 — Shawn's baked rock minutes) ───────

export interface FteDemandBakedLine {
  quoteLineItemKey: string;
  testName: string;
  taskTypeName: string;
  quantity: number;
  minutesPerTest: number;
  personHours: number;
  /** Per-test stage minutes (optional — absent from a pre-v1.1.74 backend). */
  stageMinutes?: {
    prep: number;
    test: number;
    report: number;
    review: number;
    approve: number;
  };
}

/** An unprofiled (cost-estimated) or excluded line, named for the breakdown UI. */
export interface FteDemandNamedLine {
  quoteLineItemKey: string;
  testName: string;
  quantity: number;
  lineValueCad: number;
}

/** GET /api/schedule/fte-demand — see backend fteDemandService.ts for semantics. */
export interface HybridFteDemand {
  sourceType: 'Quote';
  sourceKey: string;
  bakedPersonHours: number;
  bakedLines: FteDemandBakedLine[];
  /** Optional: absent from a pre-v1.1.72 backend (version-skew safe). */
  residualLines?: FteDemandNamedLine[];
  excludedLines?: FteDemandNamedLine[];
  residualValueCad: number;
  residualPersonHours: number;
  excludedEffortValueCad: number;
  demandPersonHours: number;
  demandFteWeeks: number;
  hourlyRate: number;
  hoursPerWeek: number;
}

export const fetchHybridFteDemand = async (quoteKey: string): Promise<HybridFteDemand> => {
  const res = await apiClient.get('/schedule/fte-demand', { params: { quoteKey } });
  return res.data as HybridFteDemand;
};
